import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';

const ALLOWED = /<\/?(p|br|b|strong|i|em|u|h1|h2|h3|h4|ul|ol|li|blockquote|a|hr|span|div|small)(\s[^>]*)?>/gi;

/** Contenu à rendre tel quel (CSS/SVG/document complet) plutôt qu'en HTML nettoyé. */
export const isFullDocument = (html = '') =>
  /<!DOCTYPE|<html[\s>]|<style[\s>]|<svg[\s>]|<link[^>]+stylesheet/i.test(html);

const stripScripts = (html) =>
  html
    .replace(/<\s*script[\s\S]*?<\s*\/\s*script\s*>/gi, '')
    .replace(/<\s*iframe[\s\S]*?<\s*\/\s*iframe\s*>/gi, '')
    .replace(/<\s*\/?\s*(script|iframe|object|embed)[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');

const wrapFragment = (html) =>
  /<html[\s>]/i.test(html)
    ? html
    : `<!DOCTYPE html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;background:transparent}</style></head><body>${html}</body></html>`;

/** Document HTML complet rendu dans une iframe isolée : le CSS de l'auteur est respecté sans polluer l'app. */
const SandboxedDocument = ({ html, className = '' }) => {
  const frameRef = useRef(null);
  const [height, setHeight] = useState(240);

  const measure = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    const body = doc?.body;
    if (!body) return;
    const style = doc.defaultView.getComputedStyle(body);
    const total =
      body.getBoundingClientRect().height +
      (parseFloat(style.marginTop) || 0) +
      (parseFloat(style.marginBottom) || 0);
    setHeight(Math.ceil(total) + 2);
  }, []);

  useEffect(() => {
    const timers = [200, 600, 1200, 2500].map((ms) => setTimeout(measure, ms));
    window.addEventListener('resize', measure);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', measure);
    };
  }, [measure, html]);

  return (
    <iframe
      ref={frameRef}
      title="Document"
      srcDoc={wrapFragment(stripScripts(html))}
      sandbox="allow-same-origin allow-popups"
      onLoad={measure}
      className={`w-full block border-0 bg-transparent ${className}`}
      style={{ height }}
      data-testid="rich-document-frame"
    />
  );
};

/** Rend le contenu rédigé par un admin : document HTML complet, HTML nettoyé, ou markdown. */
export const RichContent = ({ html = '', className = '' }) => {
  if (isFullDocument(html)) {
    return <SandboxedDocument html={html} className={className} />;
  }

  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(html);

  if (!looksLikeHtml) {
    return (
      <div className={`rich-content whitespace-pre-wrap ${className}`}>
        <ReactMarkdown>{html}</ReactMarkdown>
      </div>
    );
  }

  const safe = stripScripts(html)
    .replace(/<\s*(style)[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<[^>]+>/g, (tag) => (tag.match(ALLOWED) ? tag : ''));

  return (
    <div
      className={`rich-content ${className}`}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
};

export default RichContent;
